import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { BrandWordmark } from "@/components/Brand";
import { Menu, X, ChevronDown, LogOut, LayoutGrid } from "lucide-react";
import { useUser } from "@/lib/userContext";

const NAV = [
    { to: "/clients", label: "For Brands" },
    { to: "/doctor", label: "Dr. Aangan Theory" },
    { to: "/academy", label: "Academy" },
    { to: "/careers", label: "Careers" },
];

function UserMenu() {
    const { user, signIn, signOut } = useUser();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    if (!user) {
        return (
            <button
                onClick={() => signIn()}
                className="text-sm text-white/65 hover:text-white transition-colors"
                data-testid="nav-signin-btn"
            >
                Sign in
            </button>
        );
    }

    const initials = (user.name || user.email).slice(0, 1).toUpperCase();

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-2 text-sm text-white/85 hover:text-white"
                data-testid="nav-user-menu"
            >
                {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                ) : (
                    <div className="w-7 h-7 rounded-full bg-saffron text-black grid place-items-center text-xs font-medium">
                        {initials}
                    </div>
                )}
                <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="absolute right-0 mt-3 w-64 card-surface p-2 z-50" data-testid="nav-user-menu-dropdown">
                    <div className="px-3 py-3 border-b border-white/8 mb-2">
                        <div className="text-sm text-white truncate">{user.name}</div>
                        <div className="text-xs text-white/45 truncate">{user.email}</div>
                    </div>
                    <Link
                        to="/my"
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-3 px-3 py-2 text-sm text-white/80 hover:text-white hover:bg-white/5"
                        data-testid="nav-user-my-dashboard"
                    >
                        <LayoutGrid size={14} />
                        My Dashboard
                    </Link>
                    {user.is_admin && (
                        <Link
                            to="/admin"
                            onClick={() => setOpen(false)}
                            className="flex items-center gap-3 px-3 py-2 text-sm text-white/80 hover:text-white hover:bg-white/5"
                            data-testid="nav-user-admin"
                        >
                            <LayoutGrid size={14} />
                            Admin Console
                        </Link>
                    )}
                    <button
                        onClick={() => {
                            setOpen(false);
                            signOut();
                        }}
                        className="flex w-full items-center gap-3 px-3 py-2 text-sm text-white/65 hover:text-white hover:bg-white/5"
                        data-testid="nav-user-signout"
                    >
                        <LogOut size={14} />
                        Sign out
                    </button>
                </div>
            )}
        </div>
    );
}

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled ? "glass border-b border-white/8" : ""
            }`}
            data-testid="site-navbar"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                <Link to="/" data-testid="nav-home-link" className="flex items-center">
                    <BrandWordmark height={32} />
                </Link>

                <nav className="hidden lg:flex items-center gap-9">
                    {NAV.map((n) => {
                        const active = location.pathname === n.to;
                        return (
                            <Link
                                key={n.to}
                                to={n.to}
                                data-testid={`nav-link-${n.to.replace("/", "")}`}
                                className={`text-sm transition-colors ${
                                    active ? "text-white" : "text-white/55 hover:text-white"
                                }`}
                            >
                                {n.label}
                            </Link>
                        );
                    })}
                </nav>

                <div className="hidden lg:flex items-center gap-5">
                    <UserMenu />
                    <Link to="/clients" className="btn-primary text-xs" data-testid="nav-cta-primary">
                        Start Your Growth Journey
                    </Link>
                </div>

                <button
                    className="lg:hidden text-white"
                    onClick={() => setOpen(!open)}
                    aria-label="Menu"
                    data-testid="nav-menu-toggle"
                >
                    {open ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-white/8 glass">
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {NAV.map((n) => (
                            <Link
                                key={n.to}
                                to={n.to}
                                data-testid={`mobile-nav-${n.to.replace("/", "")}`}
                                className="text-base text-white/80"
                            >
                                {n.label}
                            </Link>
                        ))}
                        <div className="hairline" />
                        <Link to="/my" className="text-base text-white/80" data-testid="mobile-nav-my">My Dashboard</Link>
                        <UserMenu />
                        <Link to="/clients" className="btn-primary justify-center" data-testid="mobile-nav-cta">
                            Start Your Growth Journey
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
